/**
 * Player settings persistence (spec §10, plan G12). One JSON object under `eclipse.settings.v1` in
 * `localStorage`. Every storage access is wrapped in try/catch: private windows, blocked site data and quota
 * errors all fall back to the defaults (reads) or report `false` (writes) and never throw.
 *
 * Parsing is forgiving per field: a missing or invalid field takes its default while the valid ones survive,
 * so a settings object written by an older or newer build never wipes the player's other choices.
 */
import { z } from 'zod'
import { DASHBOARD_TIME_MODES, DEFAULT_SETTINGS, REDUCED_MOTION_MODES, TEXT_CONTRASTS, type Settings } from '../app/feature'
import { logger } from '../core/logger'
import { AIM_ASSIST_LEVELS } from '../data/schemas'
import { HUD_LABEL_MODES } from '../hud/reach/config'
import { QUALITY_PRESETS } from '../render/contracts'
import settingsJson from './settings.json'

export const SETTINGS_KEY = 'eclipse.settings.v1'

/** The two `Storage` methods we use; lets tests pass a fake. */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

const rangeSchema = z
  .object({ min: z.number().min(0), max: z.number().positive(), step: z.number().positive() })
  .refine((r) => r.max > r.min, 'max must exceed min')

export const settingsConfigSchema = z.object({
  /** Slider ranges for the numeric settings (the menu and the parser share them). */
  ranges: z.object({
    mouseSensitivity: rangeSchema,
    masterVolume: rangeSchema,
    musicVolume: rangeSchema,
    sfxVolume: rangeSchema,
    uiVolume: rangeSchema,
    commsVolume: rangeSchema,
    hullEffect: rangeSchema,
    /** Settings › Interface UI scale as a fraction (spec 2026-10-09 §5.5: 75–200 % in 5 % steps). */
    uiScale: rangeSchema,
  }),
  /** Gamepad menu navigation: held-direction auto-repeat (s) and left-stick thresholds (0..1). */
  menuNav: z
    .object({
      repeatDelay: z.number().positive(),
      repeatInterval: z.number().positive(),
      stickThreshold: z.number().gt(0).lt(1),
      stickRelease: z.number().gt(0).lt(1),
    })
    .refine((n) => n.stickRelease < n.stickThreshold, 'stickRelease must be below stickThreshold'),
})

export type SettingsConfig = z.infer<typeof settingsConfigSchema>
export type SettingRange = SettingsConfig['ranges']['mouseSensitivity']
export type MenuNavConfig = SettingsConfig['menuNav']

export function loadSettingsConfig(): SettingsConfig {
  const cfg = settingsConfigSchema.parse(settingsJson)
  const { mouseSensitivity, masterVolume, musicVolume, sfxVolume, uiVolume, commsVolume, hullEffect } = cfg.ranges
  for (const [name, r] of Object.entries({ masterVolume, musicVolume, sfxVolume, uiVolume, commsVolume, hullEffect })) {
    if (r.max > 1) throw new Error(`settings.json: ${name}.max must be ≤ 1`)
  }
  if (DEFAULT_SETTINGS.mouseSensitivity < mouseSensitivity.min || DEFAULT_SETTINGS.mouseSensitivity > mouseSensitivity.max) {
    throw new Error('settings.json: the default mouse sensitivity lies outside its range')
  }
  if (DEFAULT_SETTINGS.uiScale < cfg.ranges.uiScale.min || DEFAULT_SETTINGS.uiScale > cfg.ranges.uiScale.max) {
    throw new Error('settings.json: the default UI scale lies outside its range')
  }
  return cfg
}

const config = loadSettingsConfig()
const { mouseSensitivity: sens, masterVolume: vol } = config.ranges
const ranges = config.ranges

function unitSetting(r: SettingRange, fallback: number): z.ZodCatch<z.ZodNumber> {
  return z.number().min(r.min).max(r.max).catch(fallback)
}

/** Zod schema matching the `Settings` interface; each field falls back to its default on its own. */
export const settingsSchema = z.object({
  mouseSensitivity: z.number().min(sens.min).max(sens.max).catch(DEFAULT_SETTINGS.mouseSensitivity),
  invertY: z.boolean().catch(DEFAULT_SETTINGS.invertY),
  defaultFA: z.boolean().catch(DEFAULT_SETTINGS.defaultFA),
  quality: z.enum(QUALITY_PRESETS as readonly ['low', 'medium', 'high']).catch(DEFAULT_SETTINGS.quality),
  masterVolume: z.number().min(vol.min).max(vol.max).catch(DEFAULT_SETTINGS.masterVolume),
  screenShake: z.boolean().catch(DEFAULT_SETTINGS.screenShake),
  musicVolume: unitSetting(ranges.musicVolume, DEFAULT_SETTINGS.musicVolume),
  sfxVolume: unitSetting(ranges.sfxVolume, DEFAULT_SETTINGS.sfxVolume),
  uiVolume: unitSetting(ranges.uiVolume, DEFAULT_SETTINGS.uiVolume),
  commsVolume: unitSetting(ranges.commsVolume, DEFAULT_SETTINGS.commsVolume),
  hullEffect: unitSetting(ranges.hullEffect, DEFAULT_SETTINGS.hullEffect),
  nightMode: z.boolean().catch(DEFAULT_SETTINGS.nightMode),
  mono: z.boolean().catch(DEFAULT_SETTINGS.mono),
  aimAssist: z.enum(AIM_ASSIST_LEVELS as readonly ['off', 'low', 'standard', 'high']).catch(DEFAULT_SETTINGS.aimAssist),
  // Settings › Interface (spec 2026-10-09 §5.6). A save from before these existed simply takes the defaults.
  uiScale: z.number().min(ranges.uiScale.min).max(ranges.uiScale.max).catch(DEFAULT_SETTINGS.uiScale),
  textContrast: z.enum(TEXT_CONTRASTS).catch(DEFAULT_SETTINGS.textContrast),
  reducedMotion: z.enum(REDUCED_MOTION_MODES).catch(DEFAULT_SETTINGS.reducedMotion),
  hudLabels: z.enum(HUD_LABEL_MODES).catch(DEFAULT_SETTINGS.hudLabels),
  dashboardTime: z.enum(DASHBOARD_TIME_MODES).catch(DEFAULT_SETTINGS.dashboardTime),
  holdToConfirm: z.boolean().catch(DEFAULT_SETTINGS.holdToConfirm),
}) satisfies z.ZodType<Settings>

/** Any value → a complete, fresh `Settings` (non-objects give the defaults). */
export function parseSettings(raw: unknown): Settings {
  const obj = typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? raw : {}
  return settingsSchema.parse(obj)
}

/** `window.localStorage`, or null where touching it throws (sandboxed frames, blocked site data). */
export function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch (err: unknown) {
    logger.warn('localStorage is unavailable; settings will not persist', err)
    return null
  }
}

/** Reads the saved settings; anything missing, corrupt or unreadable gives the defaults. */
export function loadSettings(storage: StorageLike | null): Settings {
  if (!storage) return parseSettings(null)
  let text: string | null
  try {
    text = storage.getItem(SETTINGS_KEY)
  } catch (err: unknown) {
    logger.warn('Reading settings failed; using defaults', err)
    return parseSettings(null)
  }
  if (text === null) return parseSettings(null)
  try {
    return parseSettings(JSON.parse(text))
  } catch (err: unknown) {
    logger.warn('Saved settings are corrupt; using defaults', err)
    return parseSettings(null)
  }
}

/** Writes the settings; returns false (and logs) when storage is absent or refuses. */
export function saveSettings(storage: StorageLike | null, settings: Settings): boolean {
  if (!storage) return false
  try {
    storage.setItem(SETTINGS_KEY, JSON.stringify(parseSettings(settings)))
    return true
  } catch (err: unknown) {
    logger.warn('Saving settings failed', err)
    return false
  }
}
